<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>حجز مساحة دراسة</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="addNewBooking" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          حجز جديد
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Stats Section -->
    <v-card class="mb-4 pa-4" elevation="2">
      <v-row>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedSpaceForStats"
            :items="spaces"
            item-title="name"
            item-value="id"
            label="اختر المساحة لعرض الإحصائيات"
            class="rtl-input"
            variant="outlined"
            density="comfortable"
          ></v-select>
        </v-col>
      </v-row>
      
      <v-row class="mt-2">
        <v-col cols="12" md="2">
          <v-card class="pa-3 text-center" color="blue-lighten-5" elevation="1">
            <div class="text-h6 font-weight-bold">{{ todayStats.byHour }}</div>
            <div class="text-caption">حجز بالساعة</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="2">
          <v-card class="pa-3 text-center" color="green-lighten-5" elevation="1">
            <div class="text-h6 font-weight-bold">{{ todayStats.byDay }}</div>
            <div class="text-caption">حجز باليوم</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="2">
          <v-card class="pa-3 text-center" color="orange-lighten-5" elevation="1">
            <div class="text-h6 font-weight-bold">{{ todayStats.byWeek }}</div>
            <div class="text-caption">حجز بالأسبوع</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="2">
          <v-card class="pa-3 text-center" color="purple-lighten-5" elevation="1">
            <div class="text-h6 font-weight-bold">{{ todayStats.byMonth }}</div>
            <div class="text-caption">حجز بالشهر</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="pa-3 text-center" color="teal-lighten-5" elevation="2">
            <div class="text-h5 font-weight-bold primary--text">{{ todayStats.availableSpots }}</div>
            <div class="text-subtitle-2">مساحات متاحة اليوم</div>
            <div class="text-caption grey--text">السعة: {{ selectedSpaceCapacity }}</div>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main table -->
    <v-data-table
      :headers="headers"
      :items="bookings"
      :search="search"
      :loading="loading"
      :items-per-page="10"
      :sort-by="[{key: 'id', order: 'desc'}]"
      class="elevation-1 rtl-table"
    >
      <template #[`item.space_id`]="{ item }">
        {{ getSpaceName(item.space_id) }}
      </template>
      <template #[`item.reservation_type`]="{ item }">
        {{ getBookingTypeText(item.reservation_type) }}
      </template>
      <template #[`item.total_price`]="{ item }">
        {{ formatNumber(item.total_price) }} د.ل
      </template>
      <template #[`item.remaining`]="{ item }">
        <div :style="getRemainingCellStyle(item.remaining)">
          {{ formatNumber(item.remaining) }} د.ل
        </div>
      </template>
      <template #[`item.date_from`]="{ item }">
        {{ formatDate(item.date_from) }}
      </template>
      <template #[`item.date_to`]="{ item }">
        {{ formatDate(item.date_to) }}
      </template>
      <template #[`item.is_student`]="{ item }">
        <v-icon v-if="item.is_student" color="primary">mdi-school</v-icon>
        <v-icon v-else color="grey">mdi-account</v-icon>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          :color="getStatusColor(item.status)"
          text-color="white"
          size="small"
        >
          {{ getStatusText(item.status) }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon small class="mr-2" @click="editBooking(item)">mdi-pencil</v-icon>
        <v-icon 
          small 
          @click="confirmDeleteBooking(item.id)" 
          v-if="hasDeletePermission"
          color="error" 
          title="حذف الحجز"
        >
          mdi-delete
        </v-icon>
        <v-icon small class="mr-2" @click="showPayments(item)" title="المدفوعات">
          mdi-cash-multiple
        </v-icon>
        <v-icon small class="mr-2" @click="printBookingReceipt(item)" title="طباعة إيصال الحجز" color="primary">
          mdi-printer
        </v-icon>
      </template>
    </v-data-table>

    <!-- Add/Edit Booking Dialog -->
    <v-dialog v-model="dialog" max-width="720px" class="rtl-dialog" scrollable>
      <v-card class="rtl rounded-xl">
        <v-card-title class="pa-6 pb-2">
          <span class="text-h6 font-weight-bold">{{ formTitle }}</span>
        </v-card-title>

        <v-card-text class="pa-6 pt-4">
          <v-container fluid class="pa-3">
            <v-row dense class="booking-form-row">
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.space_id"
                  :items="spaces"
                  item-title="name"
                  item-value="id"
                  label="المساحة"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  @update:model-value="calculateTotalPrice"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.customer"
                  label="اسم العميل"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.reservation_type"
                  :items="reservationTypes"
                  item-title="text"
                  item-value="value"
                  label="نوع الحجز"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.payment_type"
                  :items="paymentTypes"
                  item-title="text"
                  item-value="value"
                  label="طريقة الدفع"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_from"
                  label="تاريخ البداية"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_to"
                  label="تاريخ النهاية"
                  type="date"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  :readonly="dateToReadonly"
                  @update:model-value="calculateTotalPrice"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.time_from"
                  label="وقت البداية"
                  type="time"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  :readonly="timeFieldsReadonly"
                  @update:model-value="calculateTotalPrice"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.time_to"
                  label="وقت النهاية"
                  type="time"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  :readonly="timeFieldsReadonly"
                  @update:model-value="calculateTotalPrice"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.total_price"
                  label="المبلغ الإجمالي (محسوب)"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  readonly
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.discount"
                  label="الخصم"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.amount"
                  label="القيمة المدفوعة"
                  type="number"
                  step="any"
                  min="0"
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  class="rtl-input"
                  suffix="د.ل"
                  :rules="paidAmountRules"
                />
              </v-col>

              <v-col cols="12" md="6" class="d-flex align-center">
                <v-switch
                  v-model="editedItem.is_student"
                  label="طالب"
                  color="primary"
                  density="comfortable"
                  hide-details
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.note"
                  label="ملاحظات"
                  variant="outlined"
                  density="comfortable"
                  rows="2"
                  hide-details="auto"
                  class="rtl-input"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn variant="text" color="error" rounded="lg" @click="close">
            إلغاء
          </v-btn>
          <v-spacer />
          <v-btn color="primary" rounded="lg" prepend-icon="mdi-check" @click="save">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">هل أنت متأكد من حذف هذا الحجز؟</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" text @click="deleteDialog = false">إلغاء</v-btn>
          <v-btn color="primary" text @click="deleteItemConfirm">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Payments Dialog -->
    <v-dialog v-model="paymentsDialog" max-width="900px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">المدفوعات</v-card-title>
        <v-card-text>
          <!-- Booking Summary -->
          <v-row class="mb-4">
            <v-col cols="12" md="3">
              <strong>العميل:</strong> {{ selectedBooking.customer }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>المساحة:</strong> {{ getSpaceName(selectedBooking.space_id) }}
            </v-col>
            <v-col cols="12" md="3">
              <strong>المبلغ الإجمالي:</strong> {{ formatNumber(selectedBooking.total_price) }} د.ل
            </v-col>
            <v-col cols="12" md="3">
              <strong>المتبقي:</strong> {{ formatNumber(Number(selectedBooking.remaining)) }} د.ل
            </v-col>
          </v-row>

          <!-- Add Payment Button -->
          <v-row class="mb-4">
            <v-col cols="12" class="text-left">
              <v-btn
                color="primary"
                @click="showAddPayment"
                :disabled="!Number(selectedBooking.remaining)"
              >
                <v-icon start>mdi-plus</v-icon>
                إضافة دفعة
              </v-btn>
            </v-col>
          </v-row>

          <!-- Payments Table -->
          <v-data-table
            :headers="paymentHeaders"
            :items="selectedBooking.payments || []"
            :key="selectedBooking.id"
            class="elevation-1"
          >
            <template #[`item.created_at`]="{ item }">
              {{ formatDate(item.created_at) }}
            </template>
            <template #[`item.amount`]="{ item }">
              {{ formatNumber(item.amount) }} د.ل
            </template>
            <template #[`item.remaining_before`]="{ item }">
              {{ formatNumber(item.remaining_before) }} د.ل
            </template>
            <template #[`item.remaining_after`]="{ item }">
              {{ formatNumber(item.remaining_after) }} د.ل
            </template>
            <template #[`item.actions`]="{ item: payment }">
              <v-icon
                small
                class="mr-2"
                @click="printReceipt(payment)"
                title="طباعة الإيصال"
              >
                mdi-printer
              </v-icon>
              <v-icon
                small
                @click="confirmDeletePayment(payment)"
                v-if="hasDeletePermission && payment.id"
                color="error"
                title="حذف الدفعة"
              >
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="paymentsDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Payment Dialog -->
    <v-dialog v-model="addPaymentDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">إضافة دفعة جديدة</v-card-title>
        <v-card-text>
          <v-form ref="paymentForm">
            <v-text-field
              v-model="newPayment.amount"
              label="المبلغ"
              type="number"
              :rules="[
                v => !!v || 'المبلغ مطلوب',
                v => Number(v) <= Number(selectedBooking.remaining) || 'المبلغ أكبر من المتبقي'
              ]"
              class="rtl-input"
            ></v-text-field>
            <v-textarea
              v-model="newPayment.note"
              label="ملاحظات"
              rows="3"
              class="rtl-input"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="addPaymentDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="primary" variant="text" @click="submitNewPayment">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Payment Confirmation Dialog -->
    <v-dialog v-model="showDeletePaymentDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">حذف الدفعة</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف هذه الدفعة؟
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue-darken-1" variant="text" @click="showDeletePaymentDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="red-darken-1" variant="text" @click="deletePaymentConfirmed">
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Receipt Section -->
    <div style="display: none">
      <div id="printSection" class="print-container">
        <div class="receipt-header">
          <div class="logo-container">
            <img src="../assets/soufara.png" alt="Logo" class="center-logo" />
          </div>
          <h2>إيصال دفع</h2>
          <p>رقم الإيصال: {{ receiptData.id }}</p>
        </div>
        <div class="receipt-details">
          <p><strong>التاريخ:</strong> {{ formatDate(receiptData.created_at) }}</p>
          <p><strong>العميل:</strong> {{ receiptData.customer }}</p>
          <p><strong>مساحة الدراسة:</strong> {{ receiptData.spaceName }}</p>
          <p><strong>طريقة الدفع:</strong> {{ getPaymentMethodText(receiptData.payment_method) }}</p>
          <p><strong>المبلغ المدفوع:</strong> {{ formatNumber(receiptData.amount) }} د.ل</p>
          <p><strong>المبلغ الإجمالي:</strong> {{ formatNumber(receiptData.totalPrice) }} د.ل</p>
          <p><strong>المبلغ المتبقي:</strong> {{ formatNumber(receiptData.remaining_after) }} د.ل</p>
          <p v-if="receiptData.note"><strong>ملاحظات:</strong> {{ receiptData.note }}</p>
        </div>
        <div class="receipt-footer">
          <p>شكراً لاختياركم مركز سفراء العلم</p>
        </div>
      </div>
    </div>

    <!-- Booking Receipt Print Template (hidden) -->
    <div id="bookingPrintSection" style="display: none;">
      <div style="font-family: 'Cairo', sans-serif; direction: rtl; max-width: 600px; margin: 0 auto; padding: 20px; color: #000;">
        
        <!-- Header with Logo -->
        <div style="text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 12px;">
          <img src="@/assets/soufara.png" alt="Logo" style="max-width: 180px; margin-bottom: 8px;">
          <h2 style="font-size: 16px; margin: 0; font-weight: 600;">مركز سفراء العلم للتدريب</h2>
        </div>

        <!-- Receipt Type -->
        <div style="text-align: center; margin-bottom: 15px;">
          <h1 style="font-size: 20px; margin: 0; font-weight: bold;">حجز مساحة دراسة</h1>
          <div style="font-size: 12px; margin-top: 3px;">التاريخ: {{ formatDate(bookingReceiptData.booking_date) }}</div>
        </div>

        <!-- Booking Details Table -->
        <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px;">
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold; width: 35%;">اسم العميل</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.customer_name }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">المساحة</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.space_name }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">من تاريخ</td>
            <td style="padding: 6px 10px; text-align: right;">{{ formatDate(bookingReceiptData.start_date) }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">إلى تاريخ</td>
            <td style="padding: 6px 10px; text-align: right;">{{ formatDate(bookingReceiptData.end_date) }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">الوقت</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.time_from }} - {{ bookingReceiptData.time_to }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">طريقة الدفع</td>
            <td style="padding: 6px 10px; text-align: right;">{{ bookingReceiptData.payment_type }}</td>
          </tr>
        </table>

        <!-- Financial Details -->
        <div style="margin: 15px 0; padding: 12px; border: 2px solid #000;">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <tr>
              <td style="padding: 5px 0; font-weight: bold;">المبلغ الإجمالي</td>
              <td style="padding: 5px 0; text-align: left; font-weight: bold;">{{ formatNumber(bookingReceiptData.total_amount) }} دينار ليبي</td>
            </tr>
            <tr style="border-top: 1px solid #ccc;">
              <td style="padding: 5px 0; font-weight: bold;">المبلغ المدفوع</td>
              <td style="padding: 5px 0; text-align: left;">{{ formatNumber(bookingReceiptData.paid_amount) }} دينار ليبي</td>
            </tr>
            <tr style="border-top: 2px solid #000;">
              <td style="padding: 5px 0; font-weight: bold; font-size: 15px;">المبلغ المتبقي</td>
              <td style="padding: 5px 0; text-align: left; font-weight: bold; font-size: 15px;">{{ formatNumber(bookingReceiptData.remaining_amount) }} دينار ليبي</td>
            </tr>
          </table>
        </div>

        <!-- Additional Info -->
        <div style="font-size: 12px; margin: 12px 0; padding: 10px 0; border-top: 1px solid #ddd;">
          <div style="margin-bottom: 5px;">
            <span style="font-weight: bold;">تم الحجز بواسطة:</span>
            <span style="margin-right: 8px;">{{ bookingReceiptData.booked_by }}</span>
          </div>
          <div>
            <span style="font-weight: bold;">تاريخ الحجز:</span>
            <span style="margin-right: 8px;">{{ formatDate(bookingReceiptData.booking_date) }}</span>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'BookSpaceComponent',
  data() {
    return {
      search: '',
      loading: false,
      dialog: false,
      deleteDialog: false,
      paymentsDialog: false,
      addPaymentDialog: false,
      showDeletePaymentDialog: false,
      selectedBooking: {},
      selectedPayment: null,
      payments: [],
      newPayment: {
        amount: '',
        note: ''
      },
      paymentHeaders: [
        { title: 'التاريخ', key: 'created_at', align: 'center' },
        { title: 'المبلغ', key: 'amount', align: 'center' },
        { title: 'المتبقي قبل', key: 'remaining_before', align: 'center' },
        { title: 'المتبقي بعد', key: 'remaining_after', align: 'center' },
        { title: 'ملاحظات', key: 'note', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      editedIndex: -1,
      editedItem: {
        id: null,
        customer: '',
        space_id: null,
        reservation_type: 'hour',
        date_from: new Date().toISOString().substr(0, 10),
        date_to: new Date().toISOString().substr(0, 10),
        time_from: '09:00',
        time_to: '10:00',
        total_price: 0,
        discount: 0,
        amount: 0,
        payment_type: 'cash',
        is_student: false,
        note: ''
      },
      defaultItem: {
        id: null,
        customer: '',
        space_id: null,
        reservation_type: 'hour',
        date_from: new Date().toISOString().substr(0, 10),
        date_to: new Date().toISOString().substr(0, 10),
        time_from: '09:00',
        time_to: '10:00',
        total_price: 0,
        discount: 0,
        amount: 0,
        payment_type: 'cash',
        is_student: false,
        note: ''
      },
      receiptData: {
        id: '',
        amount: 0,
        created_at: '',
        remaining_before: 0,
        remaining_after: 0,
        note: '',
        customer: '',
        spaceName: '',
        totalPrice: 0
      },
      bookingReceiptData: {
        customer_name: '',
        space_name: '',
        start_date: '',
        end_date: '',
        time_from: '',
        time_to: '',
        payment_type: '',
        total_amount: 0,
        paid_amount: 0,
        remaining_amount: 0,
        booked_by: '',
        booking_date: ''
      },
      headers: [
        { title: 'العميل', key: 'customer', align: 'center' },
        { title: 'المساحة', key: 'space_id', align: 'center' },
        { title: 'نوع الحجز', key: 'reservation_type', align: 'center' },
        { title: 'تاريخ البداية', key: 'date_from', align: 'center' },
        { title: 'تاريخ النهاية', key: 'date_to', align: 'center' },
        { title: 'وقت البداية', key: 'time_from', align: 'center' },
        { title: 'وقت النهاية', key: 'time_to', align: 'center' },
        { title: 'طالب', key: 'is_student', align: 'center' },
        { title: 'المبلغ الإجمالي', key: 'total_price', align: 'center' },
        { title: 'المتبقي', key: 'remaining', align: 'center' },
        { title: 'الحالة', key: 'status', align: 'center' },
        { title: 'الإجراءات', key: 'actions', sortable: false, align: 'center' }
      ],
      reservationTypes: [
        { text: 'بالساعة', value: 'hour' },
        { text: 'باليوم', value: 'day' },
        { text: 'بالأسبوع', value: 'week' },
        { text: 'بالشهر', value: 'month' }
      ],
      paymentTypes: [
        { text: 'كاش', value: 'cash' },
        { text: 'شيك', value: 'card' },
        { text: 'بطاقة ائتمان', value: 'credit_card' }
      ],
      paymentMethods: [
        { text: 'نقدي', value: 'cash' },
        { text: 'تحويل مصرفي', value: 'bank_transfer' },
        { text: 'بطاقة ائتمان', value: 'credit_card' }
      ],
      selectedSpaceForStats: null,
      paidAmountRules: [
        v =>
          v === '' ||
          v === null ||
          v === undefined ||
          (!Number.isNaN(parseFloat(v)) && parseFloat(v) >= 0) ||
          'أدخل رقماً عشرياً صحيحاً (0 أو أكثر)'
      ]
    };
  },
  computed: {
    ...mapState({
      spaces: state => state.space.spaces,
      bookings: state => state.spaceBooking.bookings,
      loading: state => state.spaceBooking.loading
    }),
    ...mapGetters('auth', ['hasPermission']),

    formTitle() {
      return this.editedIndex === -1 ? 'حجز مساحة دراسة جديدة' : 'تعديل الحجز';
    },
    
    isHourlyBooking() {
      return this.editedItem.reservation_type === 'hour';
    },
    isDailyBooking() {
      return this.editedItem.reservation_type === 'day';
    },
    isWeeklyBooking() {
      return this.editedItem.reservation_type === 'week';
    },
    isMonthlyBooking() {
      return this.editedItem.reservation_type === 'month';
    },
    
    timeFieldsReadonly() {
      return this.editedItem.reservation_type !== 'hour';
    },
    
    dateToReadonly() {
      return this.editedItem.reservation_type === 'hour' || 
             this.editedItem.reservation_type === 'week' || 
             this.editedItem.reservation_type === 'month';
    },
    
    studySpaces() {
      return [
        {
          id: 1,
          name: 'مساحة دراسة 1',
          capacity: 4,
          price_per_hour: 10,
          price_per_day: 50,
          price_per_week: 300,
          price_per_month: 1000,
          hasWifi: true,
          hasPc: true,
          hasTea: true,
          status: 'active'
        },
        {
          id: 2,
          name: 'مساحة دراسة 2',
          capacity: 2,
          price_per_hour: 8,
          price_per_day: 40,
          price_per_week: 250,
          price_per_month: 800,
          hasWifi: true,
          hasPc: false,
          hasTea: true,
          status: 'active'
        },
        {
          id: 3,
          name: 'مساحة دراسة 3',
          capacity: 6,
          price_per_hour: 15,
          price_per_day: 80,
          price_per_week: 500,
          price_per_month: 1500,
          hasWifi: true,
          hasPc: true,
          hasTea: false,
          status: 'active'
        }
      ];
    },
    userRole() {
      return this.$store.state.auth.user ? this.$store.state.auth.user.role : null;
    },
    
    isReceptionRole() {
      return this.userRole === 'reception';
    },
    
    canDeleteBookings() {
      return !this.isReceptionRole;
    },
    
    canDeletePayments() {
      return !this.isReceptionRole;
    },
    
    displayPayments() {
      return this.payments || [];
    },
    
    hasDeletePermission() {
      const user = this.$store.state.auth.user;
      const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
      
      return this.hasPermission('admin.space-bookings.destroy') || isAdmin;
    },
    
    selectedSpaceCapacity() {
      if (!this.selectedSpaceForStats) return 0;
      const space = this.spaces.find(s => s.id === this.selectedSpaceForStats);
      return space ? space.capacity : 0;
    },
    
    todayBookingsForSelectedSpace() {
      if (!this.selectedSpaceForStats) return [];
      
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      return this.bookings.filter(booking => {
        if (booking.space_id !== this.selectedSpaceForStats) return false;
        
        // Check if booking is active (not cancelled)
        if (booking.status === 'cancelled') return false;
        
        const bookingDateFrom = new Date(booking.date_from);
        bookingDateFrom.setHours(0, 0, 0, 0);
        
        const bookingDateTo = new Date(booking.date_to);
        bookingDateTo.setHours(0, 0, 0, 0);
        
        // Check if today is within the booking range
        const isToday = today >= bookingDateFrom && today <= bookingDateTo;
        if (!isToday) return false;
        
        // For hourly bookings, check if the time hasn't passed yet
        if (booking.reservation_type === 'hour') {
          // Create datetime for booking end
          const todayDateStr = new Date().toISOString().split('T')[0];
          const bookingEndDateTime = new Date(`${todayDateStr}T${booking.time_to}`);
          
          // Only count if booking end time is in the future
          return now < bookingEndDateTime;
        }
        
        // For day/week/month bookings, just check if today is in range
        return true;
      });
    },
    
    todayStats() {
      const stats = {
        byHour: 0,
        byDay: 0,
        byWeek: 0,
        byMonth: 0,
        availableSpots: this.selectedSpaceCapacity
      };
      
      if (!this.todayBookingsForSelectedSpace.length) return stats;
      
      this.todayBookingsForSelectedSpace.forEach(booking => {
        switch (booking.reservation_type) {
          case 'hour':
            stats.byHour++;
            break;
          case 'day':
            stats.byDay++;
            break;
          case 'week':
            stats.byWeek++;
            break;
          case 'month':
            stats.byMonth++;
            break;
        }
      });
      
      // Calculate available spots (capacity - total active bookings today)
      const totalActiveBookings = this.todayBookingsForSelectedSpace.length;
      stats.availableSpots = Math.max(0, this.selectedSpaceCapacity - totalActiveBookings);
      
      return stats;
    }
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.close();
      }
    },
    paymentsDialog(val) {
      if (val) {
        this.loadPayments();
      } else {
        this.selectedBooking = {};
        this.payments = [];
      }
    },
    'editedItem.reservation_type': function() {
      this.updateDateTimeFields();
    },
    
    'editedItem.date_from': function() {
      this.updateDateToField();
    },
    
    spaces: {
      handler(newSpaces) {
        if (newSpaces && newSpaces.length > 0 && !this.selectedSpaceForStats) {
          this.selectedSpaceForStats = newSpaces[0].id;
        }
        if (
          this.dialog &&
          this.editedIndex === -1 &&
          newSpaces &&
          newSpaces.length > 0 &&
          (this.editedItem.space_id == null || this.editedItem.space_id === '')
        ) {
          this.editedItem.space_id = newSpaces[0].id;
          this.$nextTick(() => this.calculateTotalPrice());
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions({
      fetchSpaces: 'space/fetchSpaces',
      fetchSpaceBookings: 'spaceBooking/fetchSpaceBookings',
      addSpaceBooking: 'spaceBooking/addSpaceBooking',
      updateSpaceBooking: 'spaceBooking/updateSpaceBooking',
      deleteSpaceBooking: 'spaceBooking/deleteSpaceBooking',
      getPayments: 'spaceBooking/getPayments',
      addPayment: 'spaceBooking/addPayment',
      deletePayment: 'spaceBooking/deletePayment'
    }),
    
    formatNumber(value) {
      return parseFloat(value).toLocaleString('ar-LY');
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('ar-LY');
    },
    getBookingTypeText(type) {
      switch (type) {
        case 'hour':
          return 'بالساعة';
        case 'day':
          return 'باليوم';
        case 'week':
          return 'بالأسبوع';
        case 'month':
          return 'بالشهر';
        default:
          return type;
      }
    },
    getSpaceName(spaceId) {
      const space = this.spaces.find(s => s.id === spaceId);
      return space ? space.name : `مساحة #${spaceId}`;
    },
    calculateRemaining(booking) {
      if (!booking || !booking.total_price) return 0;
      
      const totalPaid = this.payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
      return booking.total_price - totalPaid;
    },
    editBooking(item) {
      this.editedIndex = this.bookings.indexOf(item);
      const copy = Object.assign({}, item);
      delete copy.breaks;
      copy.amount = item.amount != null && item.amount !== '' ? parseFloat(item.amount) : 0;
      if (Number.isNaN(copy.amount)) copy.amount = 0;
      this.editedItem = copy;
      this.dialog = true;
      this.$nextTick(() => this.calculateTotalPrice());
    },
    deleteBooking(id) {
      const index = this.bookings.findIndex(booking => booking.id === id);
      if (index !== -1) {
        this.editedIndex = index;
        this.deleteDialog = true;
      }
    },
    async deleteItemConfirm() {
      try {
        const bookingId = this.bookings[this.editedIndex].id;
        await this.deleteSpaceBooking(bookingId);
        
        this.deleteDialog = false;
        this.editedIndex = -1;
        
        // Refresh the bookings list
        await this.fetchSpaceBookings();
        
        this.$store.dispatch('showAlert', {
          message: 'تم حذف الحجز بنجاح',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting booking:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء حذف الحجز',
          type: 'error'
        });
      }
    },
    close() {
      this.dialog = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    },
    updateDateTimeFields() {
      if (this.editedItem.reservation_type !== 'hour') {
        this.editedItem.time_from = '09:00';
        this.editedItem.time_to = '20:00';
      }
      
      this.updateDateToField();
    },
    updateDateToField() {
      if (!this.editedItem.date_from) return;
      
      const dateFrom = new Date(this.editedItem.date_from);
      
      switch (this.editedItem.reservation_type) {
        case 'hour':
          this.editedItem.date_to = this.editedItem.date_from;
          break;
          
        case 'day':
          break;
          
        case 'week':
          {
            const weekEndDate = new Date(dateFrom);
            weekEndDate.setDate(dateFrom.getDate() + 6);
            this.editedItem.date_to = weekEndDate.toISOString().substr(0, 10);
          }
          break;
          
        case 'month':
          {
            const monthEndDate = new Date(dateFrom);
            monthEndDate.setDate(dateFrom.getDate() + 29);
            this.editedItem.date_to = monthEndDate.toISOString().substr(0, 10);
          }
          break;
      }
      
      this.calculateTotalPrice();
    },
    calculateTotalPrice() {
      if (!this.editedItem.space_id) return;

      const space = this.spaces.find(s => s.id === this.editedItem.space_id);
      if (!space) return;

      let basePrice = 0;

      switch (this.editedItem.reservation_type) {
        case 'hour':
          if (this.editedItem.time_from && this.editedItem.time_to) {
            const [fromHours, fromMinutes] = this.editedItem.time_from.split(':').map(Number);
            const [toHours, toMinutes] = this.editedItem.time_to.split(':').map(Number);

            const fromTotalMinutes = fromHours * 60 + fromMinutes;
            const toTotalMinutes = toHours * 60 + toMinutes;

            const diffMinutes = toTotalMinutes - fromTotalMinutes;

            const hours = Math.ceil(diffMinutes / 60);

            if (hours > 0) {
              basePrice = hours * (space.price_per_hour || 0);
              this.editedItem.total_price = basePrice;
            }
          }
          break;

        case 'day':
          if (this.editedItem.date_from && this.editedItem.date_to) {
            const fromDate = new Date(this.editedItem.date_from);
            const toDate = new Date(this.editedItem.date_to);
            const days = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
            if (days > 0) {
              basePrice = days * (space.price_per_day || 0);
              this.editedItem.total_price = basePrice;
            }
          }
          break;

        case 'week':
          basePrice = space.price_per_week || 0;
          this.editedItem.total_price = basePrice;
          break;

        case 'month':
          basePrice = space.price_per_month || 0;
          this.editedItem.total_price = basePrice;
          break;
      }
    },
    async save() {
      try {
        const raw = Object.assign({}, this.editedItem);
        delete raw.breaks;
        const bookingData = {
          ...raw,
          coffee_breaks: [],
          total_price: parseFloat(this.editedItem.total_price),
          discount: parseFloat(this.editedItem.discount) || 0,
          amount: parseFloat(this.editedItem.amount) || 0
        };
        
        if (this.editedIndex > -1) {
          await this.updateSpaceBooking({ 
            id: bookingData.id, 
            bookingData 
          });
          this.$store.dispatch('showAlert', {
            message: 'تم تحديث الحجز بنجاح',
            type: 'success'
          });
        } else {
          await this.addSpaceBooking(bookingData);
          this.$store.dispatch('showAlert', {
            message: 'تم إضافة الحجز بنجاح',
            type: 'success'
          });
        }
        this.close();
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء حفظ الحجز',
          type: 'error'
        });
      }
    },
    async showPayments(item) {
      console.log('Selected booking:', item);
      console.log('Payments in booking:', item.payments);
      
      this.selectedBooking = Object.assign({}, item);
      
      if (item.payments && Array.isArray(item.payments)) {
        console.log('Using payments from booking:', item.payments);
        this.payments = item.payments;
      } else {
        console.log('No payments in booking, loading from API');
        await this.loadPayments();
      }
      
      console.log('Final payments array:', this.payments);
      this.paymentsDialog = true;
    },
    async loadPayments() {
      try {
        const response = await this.getPayments({
          bookingId: this.selectedBooking.id,
          paymentable_type: 'App\\Models\\SpaceBooking'
        });
        
        if (response && response.data) {
          this.payments = response.data;
        } else {
          this.payments = [];
        }
      } catch (error) {
        console.error('Error loading payments:', error);
        this.payments = [];
      }
    },
    showAddPayment() {
      this.newPayment = {
        amount: '',
        note: ''
      };
      this.addPaymentDialog = true;
    },
    async submitNewPayment() {
      if (!this.$refs.paymentForm.validate()) return;
      
      try {
        const paymentData = {
          space_booking_id: this.selectedBooking.id,
          amount: parseFloat(this.newPayment.amount),
          note: this.newPayment.note || ''
        };
        
        console.log('Submitting payment data:', paymentData);
        
        const response = await this.addPayment(paymentData);
        console.log('Payment response:', response);
        
        this.$store.dispatch('showAlert', {
          message: 'تمت إضافة الدفعة بنجاح',
          type: 'success'
        });
        
        this.addPaymentDialog = false;
        
        if (this.selectedBooking.payments) {
          await this.fetchSpaceBookings();
          const updatedBooking = this.bookings.find(b => b.id === this.selectedBooking.id);
          if (updatedBooking) {
            this.selectedBooking = Object.assign({}, updatedBooking);
          }
        } else {
          await this.loadPayments();
        }
      } catch (error) {
        console.error('Error adding payment:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء إضافة الدفعة',
          type: 'error'
        });
      }
    },
    confirmDeletePayment(payment) {
      this.selectedPayment = payment;
      this.showDeletePaymentDialog = true;
    },
    async deletePaymentConfirmed() {
      try {
        await this.deletePayment({
          paymentId: this.selectedPayment.id
        });
        
        this.$store.dispatch('showAlert', {
          message: 'تم حذف الدفعة بنجاح',
          type: 'success'
        });
        
        this.showDeletePaymentDialog = false;
        
        if (this.selectedBooking.payments) {
          await this.fetchSpaceBookings();
          const updatedBooking = this.bookings.find(b => b.id === this.selectedBooking.id);
          if (updatedBooking) {
            this.selectedBooking = Object.assign({}, updatedBooking);
          }
        } else {
          await this.loadPayments();
        }
      } catch (error) {
        console.error('Error deleting payment:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء حذف الدفعة',
          type: 'error'
        });
      }
    },
    printReceipt(payment) {
      this.receiptData = {
        id: payment.id,
        amount: payment.amount,
        created_at: payment.created_at,
        remaining_before: payment.remaining_before,
        remaining_after: payment.remaining_after,
        note: payment.note || '',
        customer: this.selectedBooking.customer,
        spaceName: this.getSpaceName(this.selectedBooking.space_id),
        totalPrice: this.selectedBooking.total_price
      };
      
      this.$nextTick(() => {
        const printContent = document.getElementById('printSection');
        const originalContents = document.body.innerHTML;
        
        document.body.innerHTML = printContent.innerHTML;
        document.body.style.direction = 'rtl';
        document.body.style.fontFamily = "'Cairo', sans-serif";
        
        window.print();
        
        document.body.innerHTML = originalContents;
      });
    },
    getStatusColor(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'error';
        default:
          return 'grey';
      }
    },
    getStatusText(status) {
      switch (status) {
        case 'completed':
          return 'مكتمل';
        case 'pending':
          return 'مستمر';
        case 'in_progress':
          return 'مستمر';
        case 'cancelled':
          return 'ملغي';
        default:
          return status;
      }
    },
    /** First space chosen by default when creating a booking */
    ensureFirstSpaceSelected() {
      if (this.spaces && this.spaces.length > 0) {
        this.editedItem.space_id = this.spaces[0].id;
      } else {
        this.editedItem.space_id = null;
      }
    },
    addNewBooking() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.ensureFirstSpaceSelected();
      this.updateDateTimeFields();
      this.dialog = true;
      this.$nextTick(() => this.calculateTotalPrice());
    },
    getPaymentMethodText(method) {
      if (!method || !this.paymentMethods) return '';
      const paymentMethod = this.paymentMethods.find(m => m.value === method);
      return paymentMethod ? paymentMethod.text : method;
    },
    getRemainingCellStyle(remaining) {
      const amount = Number(remaining) || 0;
      if (amount > 0) {
        return {
          backgroundColor: '#ffebee',
          padding: '16px',
          margin: '-16px',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      } else {
        return {
          backgroundColor: '#e8f5e9',
          padding: '16px',
          margin: '-16px',
          fontWeight: 'bold',
          textAlign: 'center'
        };
      }
    },
    confirmDeleteBooking(id) {
      this.editedIndex = this.bookings.findIndex(b => b.id === id);
      this.deleteDialog = true;
    },
    async printBookingReceipt(booking) {
      try {
        // Calculate paid amount
        const totalAmount = booking.total_price - (booking.discount || 0);
        const paidAmount = totalAmount - (booking.remaining || 0);
        
        // Get space name
        const spaceName = this.getSpaceName(booking.space_id);
        
        // Get booked by (username from auth or created_by field)
        const user = this.$store.state.auth.user;
        const bookedBy = booking.created_by_name || user?.name || 'غير معروف';
        
        // Get payment type text
        const paymentTypeObj = this.paymentTypes.find(pt => pt.value === booking.payment_type);
        const paymentTypeText = paymentTypeObj ? paymentTypeObj.text : booking.payment_type || 'غير محدد';
        
        // Prepare receipt data
        this.bookingReceiptData = {
          customer_name: booking.customer,
          space_name: spaceName,
          start_date: booking.date_from,
          end_date: booking.date_to,
          time_from: booking.time_from,
          time_to: booking.time_to,
          payment_type: paymentTypeText,
          total_amount: booking.total_price,
          paid_amount: paidAmount,
          remaining_amount: booking.remaining || 0,
          booked_by: bookedBy,
          booking_date: booking.created_at || new Date().toISOString()
        };
        
        // Wait for next tick to ensure DOM is updated
        await this.$nextTick();
        
        // Create print window
        const printContent = document.getElementById('bookingPrintSection').innerHTML;
        const printWindow = window.open('', '_blank');
        
        printWindow.document.write(`
          <!DOCTYPE html>
          <html dir="rtl">
            <head>
              <title>إيصال حجز مساحة دراسة</title>
              <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap" rel="stylesheet">
              <style>
                @page {
                  size: A4;
                  margin: 1cm;
                }
                body {
                  font-family: 'Cairo', sans-serif;
                  direction: rtl;
                  margin: 0;
                  padding: 0;
                }
                @media print {
                  body {
                    print-color-adjust: exact;
                    -webkit-print-color-adjust: exact;
                  }
                }
              </style>
            </head>
            <body>
              ${printContent}
            </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = function() {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = function() {
              printWindow.close();
            };
          }, 250);
        };
      } catch (error) {
        console.error('Error printing booking receipt:', error);
        this.$store.dispatch('showAlert', {
          message: 'حدث خطأ أثناء طباعة الإيصال',
          type: 'error'
        });
      }
    },
  },
  async created() {
    try {
      await this.fetchSpaces();

      if (this.spaces && this.spaces.length > 0) {
        this.selectedSpaceForStats = this.spaces[0].id;
      }

      await this.fetchSpaceBookings();
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.$store.dispatch('showAlert', {
        message: 'حدث خطأ أثناء تحميل البيانات',
        type: 'error'
      });
    }
  }
};
</script>

<!-- BookSpace specific styles only -->
<style scoped>
.rtl-table {
  direction: rtl;
}

.payment-dialog {
  padding: 20px;
}

.summary-row {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.payment-table {
  margin-top: 20px;
}

.receipt-header {
  text-align: center;
  margin-bottom: 20px;
}

.logo-container {
  margin-bottom: 10px;
}

.center-logo {
  max-width: 150px;
}

.receipt-details {
  margin-bottom: 30px;
}

.receipt-footer {
  text-align: center;
  margin-top: 30px;
  border-top: 1px solid #ddd;
  padding-top: 20px;
}

.print-container {
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  padding: 20px;
}

.booking-form-row :deep(.v-field) {
  margin-bottom: 2px;
}
</style> 